import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown, ChevronRight, Plus, Minus,
  Layers, BarChart3, Upload, Download,
  FileSpreadsheet, RefreshCw, X, GitCompare, Percent, CalendarDays, Check,
} from 'lucide-react';
import { Button }   from '../components/ui/button';
import { Input }    from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger }  from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import { actualsAPI, aopAPI } from '../lib/api';
import { cn }       from '../lib/utils';
import { useNumberFormat, applyFormat } from '../context/NumberFormatContext';
import { toast }    from 'sonner';

// ─── Searchable select ────────────────────────────────────────────────────────

function SearchableSelect({ value, onValueChange, options, placeholder = 'Select…', className }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const filtered = options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));
  const selected = options.find(o => o.value === value);
  return (
    <Popover open={open} onOpenChange={o => { setOpen(o); if (!o) setSearch(''); }}>
      <PopoverTrigger asChild>
        <button className={cn(
          'flex items-center justify-between h-8 px-3 text-sm rounded-lg border border-slate-200 bg-white hover:bg-slate-50 gap-2 transition-colors',
          className
        )}>
          <span className="truncate text-slate-700">{selected?.label || placeholder}</span>
          <ChevronDown className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-56" align="start">
        <div className="p-2 border-b border-slate-100">
          <Input placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)}
            className="h-7 text-xs" autoFocus />
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {filtered.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No results</p>}
          {filtered.map(opt => (
            <button key={opt.value}
              onClick={() => { onValueChange(opt.value); setOpen(false); setSearch(''); }}
              className={cn(
                'w-full text-left text-sm py-1.5 hover:bg-slate-50 flex items-center gap-2',
                opt.isParent && 'font-semibold text-slate-800 bg-slate-50/80 border-t border-slate-100 first:border-t-0',
                opt.isChild  && 'text-slate-600',
                opt.isMyntraChild && 'text-primary-600',
                value === opt.value && 'bg-primary-50 text-primary-700 font-medium'
              )}
              style={{ paddingLeft: opt.isMyntraChild ? '3rem' : opt.isChild ? '2rem' : '0.75rem', paddingRight: '0.75rem' }}
            >
              {opt.isMyntraChild && <span className="text-slate-300 flex-shrink-0 text-xs">↳</span>}
              {opt.isChild && !opt.isMyntraChild && <span className="text-slate-300 flex-shrink-0">↳</span>}
              <span className="truncate">{opt.label}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── Multi-select ──────────────────────────────────────────────────────────────
// `values` is an array of selected option values. 'all' is exclusive with any other selection.

function MultiSelect({ values, onValuesChange, options, placeholder = 'Select…', className }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const filtered = options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));
  const isAll = values.includes('all');

  const toggle = (val) => {
    if (val === 'all') { onValuesChange(['all']); return; }
    const base = isAll ? [] : values;
    const next = base.includes(val) ? base.filter(v => v !== val) : [...base, val];
    onValuesChange(next.length ? next : ['all']);
  };

  const label = isAll
    ? placeholder
    : values.length === 1
      ? (options.find(o => o.value === values[0])?.label || values[0])
      : `${values.length} selected`;

  return (
    <Popover open={open} onOpenChange={o => { setOpen(o); if (!o) setSearch(''); }}>
      <PopoverTrigger asChild>
        <button className={cn(
          'flex items-center justify-between h-8 px-3 text-sm rounded-lg border border-slate-200 bg-white hover:bg-slate-50 gap-2 transition-colors',
          className
        )}>
          <span className="truncate text-slate-700">{label}</span>
          <ChevronDown className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-56" align="start">
        <div className="p-2 border-b border-slate-100">
          <Input placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)}
            className="h-7 text-xs" autoFocus />
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {filtered.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No results</p>}
          {filtered.map(opt => {
            const checked = opt.value === 'all' ? isAll : (!isAll && values.includes(opt.value));
            return (
              <button key={opt.value}
                onClick={() => toggle(opt.value)}
                className={cn(
                  'w-full text-left text-sm py-1.5 hover:bg-slate-50 flex items-center gap-2',
                  opt.isParent && 'font-semibold text-slate-800 bg-slate-50/80 border-t border-slate-100 first:border-t-0',
                  opt.isChild  && 'text-slate-600',
                  opt.isMyntraChild && 'text-primary-600',
                  checked && 'bg-primary-50 text-primary-700 font-medium'
                )}
                style={{ paddingLeft: opt.isMyntraChild ? '3rem' : opt.isChild ? '2rem' : '0.75rem', paddingRight: '0.75rem' }}
              >
                <span className={cn(
                  'w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0',
                  checked ? 'bg-primary-600 border-primary-600' : 'border-slate-300'
                )}>
                  {checked && <Check className="w-2.5 h-2.5 text-white" />}
                </span>
                {opt.isMyntraChild && <span className="text-slate-300 flex-shrink-0 text-xs">↳</span>}
                {opt.isChild && !opt.isMyntraChild && <span className="text-slate-300 flex-shrink-0">↳</span>}
                <span className="truncate">{opt.label}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── Time periods ──────────────────────────────────────────────────────────────

const TIME_PERIODS = {
  monthly:     { label: 'Monthly',   periods: 12 },
  quarterly:   { label: 'Quarterly', periods: 4  },
  half_yearly: { label: '6 Months',  periods: 2  },
  annually:    { label: 'Annual',    periods: 1  },
};

// ─── Channel hierarchy ─────────────────────────────────────────────────────────
// Actuals: Myntra is split into MSOR / MNOW / SJIT / PPMP sub-channels

const CHANNEL_OPTIONS = [
  { value: 'all',            label: 'All Channels' },
  { value: 'd2c',            label: 'D2C',           isParent: true },
  { value: 'website',        label: 'Website',        isChild: true },
  { value: 'quick_commerce', label: 'Quick Commerce', isParent: true },
  { value: 'blinkit',        label: 'Blinkit',        isChild: true },
  { value: 'swiggy',         label: 'Swiggy',         isChild: true },
  { value: 'zepto',          label: 'Zepto',          isChild: true },
  { value: 'knot',           label: 'Knot',           isChild: true },
  { value: 'slikk',          label: 'Slikk',          isChild: true },
  { value: 'zilo',           label: 'Zilo',           isChild: true },
  { value: 'marketplaces',   label: 'Marketplaces',   isParent: true },
  { value: 'amazon',         label: 'Amazon',         isChild: true },
  { value: 'myntra',         label: 'Myntra',         isChild: true, isParent: true },
  { value: 'msor',           label: 'MSOR',           isChild: true, isMyntraChild: true },
  { value: 'mnow',           label: 'MNOW',           isChild: true, isMyntraChild: true },
  { value: 'sjit',           label: 'SJIT',           isChild: true, isMyntraChild: true },
  { value: 'ppmp',           label: 'PPMP',           isChild: true, isMyntraChild: true },
  { value: 'flipkart',       label: 'Flipkart',       isChild: true },
  { value: 'eternz',         label: 'Eternz',         isChild: true },
  { value: 'nykaa',          label: 'Nykaa',          isChild: true },
  { value: 'ajio',           label: 'Ajio',           isChild: true },
  { value: 'meesho',         label: 'Meesho',         isChild: true },
];

// Channel → leaf channels (used for aggregation)
const PARENT_CHILDREN = {
  d2c:            ['website'],
  quick_commerce: ['blinkit','swiggy','zepto','knot','slikk','zilo'],
  marketplaces:   ['amazon','msor','mnow','sjit','ppmp','flipkart','eternz','nykaa','ajio','meesho'],
  myntra:         ['msor','mnow','sjit','ppmp'],
};

// True leaf channels that have DB documents
const LEAF_CHANNELS = CHANNEL_OPTIONS.filter(c => c.isChild && !c.isParent);

// Myntra sub-channels for grouped display
const MYNTRA_SUBS = ['msor','mnow','sjit','ppmp'];

// ─── Category options ──────────────────────────────────────────────────────────

const CATEGORY_OPTIONS = [
  { value: 'all',                    label: 'All Categories' },
  // Womens Jewellery
  { value: 'womens_earrings',        label: 'Womens Earrings' },
  { value: 'womens_necklaces',       label: 'Womens Necklaces' },
  { value: 'womens_bracelets',       label: 'Womens Bracelets' },
  { value: 'womens_rings',           label: 'Womens Rings' },
  { value: 'womens_anklets',         label: 'Womens Anklets' },
  { value: 'womens_jewellery_sets',  label: 'Womens Jewellery Sets' },
  { value: 'womens_waist_chain',     label: 'Womens Waist Chain' },
  // Mens Jewellery
  { value: 'mens_earrings',          label: 'Mens Earrings' },
  { value: 'mens_necklaces',         label: 'Mens Necklaces' },
  { value: 'mens_bracelets',         label: 'Mens Bracelets' },
  { value: 'mens_rings',             label: 'Mens Rings' },
  { value: 'mens_brooches',          label: 'Mens Brooches' },
  { value: 'mens_jewellery_set',     label: 'Mens Jewellery Set' },
  { value: 'rings',                  label: 'Rings' },
  { value: 'kids_jewellery_sets',    label: 'Kids Jewellery sets' },
  // Watches & Eyewear
  { value: 'watches',                label: 'Watches' },
  { value: 'watch_stack',            label: 'Watch Stack' },
  { value: 'womens_sunglasses',      label: 'Womens Sunglasses' },
  { value: 'mens_sunglasses',        label: 'Mens Sunglasses' },
  // Accessories / Lifestyle
  { value: 'charms',                 label: 'Charms' },
  { value: 'hair_accessories',       label: 'Hair Accessories' },
  { value: 'hand_accessories',       label: 'Hand Accessories' },
  { value: 'caps',                   label: 'Caps' },
  { value: 'hats',                   label: 'Hats' },
  { value: 'womens_scarves',         label: 'Womens Scarves' },
  { value: 'mens_scarves',           label: 'Mens Scarves' },
  { value: 'belts',                  label: 'Belts' },
  { value: 'bags',                   label: 'Bags' },
  { value: 'keychains',              label: 'Keychains' },
  // Gift Boxes & Bundles
  { value: 'women_gift_boxes',       label: 'Women Gift Boxes' },
  { value: 'men_gift_boxes',         label: 'Men Gift Boxes' },
  { value: 'women_bundles',          label: 'Women Bundles' },
  { value: 'men_bundles',            label: 'Men Bundles' },
  { value: 'rakhi',                  label: 'Rakhi' },
  // Other
  { value: 'add_on',                 label: 'Add-on' },
  { value: 'packaging',              label: 'Packaging' },
  { value: 'unknown',                label: 'Unknown' },
];

// Maps MongoDB AOP category name → slug ID used in CATEGORY_OPTIONS
const CAT_NAME_TO_ID = {
  'Add-on':               'add_on',
  'Bags':                 'bags',
  'Belts':                'belts',
  'Caps':                 'caps',
  'Charms':               'charms',
  'Hair Accessories':     'hair_accessories',
  'Hand Accessories':     'hand_accessories',
  'Hats':                 'hats',
  'Keychains':            'keychains',
  'Kids Jewellery sets':  'kids_jewellery_sets',
  'Men Bundles':          'men_bundles',
  'Men Gift Boxes':       'men_gift_boxes',
  'Mens Bracelets':       'mens_bracelets',
  'Mens Brooches':        'mens_brooches',
  'Mens Earrings':        'mens_earrings',
  'Mens Jewellery Set':   'mens_jewellery_set',
  'Mens Necklaces':       'mens_necklaces',
  'Mens Rings':           'mens_rings',
  'Mens Scarves':         'mens_scarves',
  'Mens Sunglasses':      'mens_sunglasses',
  'Packaging':            'packaging',
  'Rakhi':                'rakhi',
  'Rings':                'rings',
  'Unknown':              'unknown',
  'Watch Stack':          'watch_stack',
  'Watches':              'watches',
  'Women Bundles':        'women_bundles',
  'Women Gift Boxes':     'women_gift_boxes',
  'Womens Anklets':       'womens_anklets',
  'Womens Bracelets':     'womens_bracelets',
  'Womens Earrings':      'womens_earrings',
  'Womens Jewellery Sets':'womens_jewellery_sets',
  'Womens Necklaces':     'womens_necklaces',
  'Womens Rings':         'womens_rings',
  'Womens Scarves':       'womens_scarves',
  'Womens Sunglasses':    'womens_sunglasses',
  'Womens Waist Chain':   'womens_waist_chain',
};

// ─── P&L structure (identical to AOP Planning) ────────────────────────────────

const PNL_STRUCTURE = [
  { id: 'gross_revenue_incl_gst', name: 'Gross Sales', expandable: true, hasChannelBreakdown: true, hasCategoryBreakdown: true },
  {
    id: 'discounts_block', name: 'Less : Discounts', type: 'cost', expandable: true, calculated: true,
    children: [
      { id: 'less_discounts', name: 'Discount', type: 'deduction', expandable: true, hasChannelBreakdown: true, hasCategoryBreakdown: true },
    ]
  },
  { id: 'total_revenue_incl_gst', name: 'Total Sales', type: 'subtotal', calculated: true },
  {
    id: 'adjustments_block', name: 'Less : Returns & Adjustments', type: 'cost', expandable: true, calculated: true,
    children: [
      { id: 'less_cancellations', name: 'Cancellations', type: 'deduction', expandable: true, hasChannelBreakdown: true, hasCategoryBreakdown: true },
      { id: 'less_rto',           name: 'RTO',           type: 'deduction', expandable: true, hasChannelBreakdown: true, hasCategoryBreakdown: true },
      { id: 'less_returns',       name: 'Returns',       type: 'deduction', expandable: true, hasChannelBreakdown: true, hasCategoryBreakdown: true },
      { id: 'less_discrepancy',   name: 'Discrepancy',   type: 'deduction', expandable: true, hasChannelBreakdown: true, hasCategoryBreakdown: true },
    ]
  },
  { id: 'net_revenue', name: 'Net Sales (1)', type: 'subtotal', calculated: true, highlight: true },
  {
    id: 'cogs', name: 'COGS', type: 'cost', expandable: true, calculated: true,
    children: [
      { id: 'cogs_product',   name: 'Material Cost', type: 'deduction', expandable: true, hasChannelBreakdown: true, hasCategoryBreakdown: true },
      { id: 'cogs_packaging', name: 'Packing',        type: 'deduction', expandable: true, hasChannelBreakdown: true, hasCategoryBreakdown: true },
    ]
  },
  { id: 'gross_margin', name: 'Gross Margin (2)', type: 'subtotal', calculated: true, highlight: true },
  {
    id: 'direct_costs', name: 'Direct Costs', type: 'cost', expandable: true, calculated: true,
    children: [
      { id: 'payment_gateway',    name: 'Payment Gateway',             type: 'deduction', expandable: true, hasChannelBreakdown: true, hasCategoryBreakdown: true },
      { id: 'logistics_shipping', name: 'Logistics',                   type: 'deduction', expandable: true, hasChannelBreakdown: true, hasCategoryBreakdown: true },
      { id: 'channel_margin',     name: 'Channel Margin / Commission', type: 'deduction', expandable: true, hasChannelBreakdown: true, hasCategoryBreakdown: true },
    ]
  },
  { id: 'cm1', name: 'CM1 (3)', type: 'subtotal', calculated: true, highlight: true },
  {
    id: 'direct_marketing_section', name: 'Performance Marketing', type: 'cost', expandable: true, calculated: true,
    children: [
      { id: 'fb_spend', name: 'D2C / Website', expandable: true, children: [
        { id: 'meta_spend',       name: 'Meta' },
        { id: 'google_spend',     name: 'Google' },
        { id: 'affiliate_spend',  name: 'Affiliate' },
        { id: 'retention_spend',  name: 'Retention' },
      ]},
      { id: 'marketplace_marketing', name: 'Marketplaces', type: 'cost', expandable: true, calculated: true, children: [
        { id: 'amazon_ads',   name: 'Amazon' },
        { id: 'flipkart_ads', name: 'Flipkart' },
        { id: 'myntra_ads',   name: 'Myntra' },
        { id: 'eternz_ads',   name: 'Eternz' },
        { id: 'meesho_ads',   name: 'Meesho' },
        { id: 'nykaa_ads',    name: 'Nykaa' },
        { id: 'ajio_ads',     name: 'Ajio' },
      ]},
      { id: 'qc_marketing', name: 'Quick Commerce', type: 'cost', expandable: true, calculated: true, children: [
        { id: 'blinkit_ads', name: 'Blinkit' },
        { id: 'zepto_ads',   name: 'Zepto' },
        { id: 'knot_ads',    name: 'Knot' },
        { id: 'slikk_ads',   name: 'Slikk' },
        { id: 'swiggy_ads',  name: 'Swiggy' },
        { id: 'zilo_ads',    name: 'Zilo' },
      ]},
    ]
  },
  { id: 'cm2', name: 'CM2 (4)', type: 'subtotal', calculated: true, highlight: true, primary: true },
  {
    id: 'corporate_overheads_section', name: 'Corporate Overheads', type: 'cost', expandable: true, calculated: true,
    children: [
      { id: 'employee_cost', name: 'Employee Cost' },
      { id: 'brand_marketing', name: 'Brand & Marketing (Excl. Performance)', type: 'cost', expandable: true, calculated: true, children: [
        { id: 'content_influencer',   name: 'Content Influencer' },
        { id: 'brand_identity',       name: 'Brand Identity' },
        { id: 'branding_content',     name: 'Branding Content' },
        { id: 'seo',                  name: 'SEO' },
        { id: 'pr_agency',            name: 'PR Agency' },
        { id: 'influencer_marketing', name: 'Influencer Marketing' },
        { id: 'photography',          name: 'Photography' },
        { id: 'inhouse_shoots',       name: 'Monthly Inhouse Shoots' },
        { id: 'brand_activation',     name: 'Brand Activation' },
        { id: 'brand_film',           name: 'Brand Film' },
        { id: 'brand_other',          name: 'Other' },
      ]},
      { id: 'tech_expenses', name: 'Tech Overheads', type: 'cost', expandable: true, calculated: true, children: [
        { id: 'website_tools',          name: 'Website Tools' },
        { id: 'ops_tools',              name: 'Ops Tools' },
        { id: 'customer_support_tools', name: 'Customer Support Tools' },
        { id: 'website_dev',            name: 'Website Development' },
        { id: 'data_tools',             name: 'Data Tools' },
        { id: 'other_tools',            name: 'Other Tools' },
        { id: 'retention_tools',        name: 'Retention Tools' },
        { id: 'retention_spends',       name: 'Retention Spends (SMS/Email)' },
      ]},
      { id: 'staff_welfare',         name: 'Staff Welfare' },
      { id: 'professional_expenses', name: 'Professional & Agency' },
      { id: 'office_exp',            name: 'Office Expenses' },
      { id: 'warehouse_exp',         name: 'Warehouse Expenses' },
      { id: 'rental_expenses',       name: 'Rental' },
      { id: 'travel_conveyance',     name: 'Travel & Conveyance' },
      { id: 'damaged_goods',         name: 'Damaged Goods Provision' },
    ]
  },
  { id: 'ebitda', name: 'EBITDA (5)', type: 'subtotal', calculated: true, highlight: true, primary: true },
];

// ─── Data utilities ────────────────────────────────────────────────────────────

const round2    = v => Math.round((v || 0) * 100) / 100;
const absVal    = v => Math.abs(v || 0);
const TO_RUPEES = 100000;
const toRupees  = arr => arr.map(v => round2(v * TO_RUPEES));
const sumArrays = (...arrs) => Array((arrs[0] || []).length || 12).fill(0)
  .map((_,i) => arrs.reduce((s,a) => s + (a?.[i] || 0), 0));

const aggregatePeriods = (monthly12, periods) => {
  if (!monthly12?.length) return Array(periods).fill(0);
  if (periods === 12) return monthly12.map(round2);
  if (periods === 4)  return [0,1,2,3].map(q => round2(monthly12.slice(q*3,q*3+3).reduce((a,b)=>a+b,0)));
  if (periods === 2)  return [round2(monthly12.slice(0,6).reduce((a,b)=>a+b,0)), round2(monthly12.slice(6).reduce((a,b)=>a+b,0))];
  if (periods === 1)  return [round2(monthly12.reduce((a,b)=>a+b,0))];
  return monthly12.map(round2);
};

const slugify = name => name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

const getFYMonths = (fy) => {
  const sy = String((fy - 1) % 100).padStart(2, '0');
  const ey = String(fy % 100).padStart(2, '0');
  return ['Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec','Jan','Feb','Mar']
    .map((m, i) => `${m}-${i < 9 ? sy : ey}`);
};

const API_TO_ROW = {
  gross_sales:'gross_revenue_incl_gst', discount:'less_discounts',
  cancellations:'less_cancellations', rto:'less_rto', returns:'less_returns',
  discrepancy:'less_discrepancy', material_cost:'cogs_product', packing:'cogs_packaging',
  payment_gateway:'payment_gateway', logistics:'logistics_shipping', channel_margins:'channel_margin',
};
const ROW_TO_API = Object.fromEntries(Object.entries(API_TO_ROW).map(([k,v]) => [v,k]));
const NEGATIVE_KEYS = new Set(['discount','cancellations','rto','returns','discrepancy','material_cost','packing','payment_gateway','logistics','channel_margins']);

const EMPTY_RAW = { overall:{}, byChannel:{}, byCategory:{}, byChanCat:{}, channelMeta:{}, categoryMeta:{} };

// ── DateRangePickerPopover ─────────────────────────────────────────────────────
// Isolated component so internal picker state never re-renders the parent page.
function DateRangePickerPopover({ confirmed, onConfirm, fyStart, fyEnd, visibleCount }) {
  const [open,        setOpen]        = useState(false);
  const [pickerRange, setPickerRange] = useState(confirmed);

  // Sync picker when confirmed range changes externally (e.g. clear)
  React.useEffect(() => { setPickerRange(confirmed); }, [confirmed]);

  const close = () => setOpen(false);

  const apply = (range) => {
    onConfirm(range);
    close();
  };

  const PRESETS = React.useMemo(() => [
    { label: 'This month',   fn: () => { const n = new Date(); return { from: new Date(n.getFullYear(), n.getMonth(), 1),     to: new Date(n.getFullYear(), n.getMonth() + 1, 0) }; } },
    { label: 'Last month',   fn: () => { const n = new Date(); return { from: new Date(n.getFullYear(), n.getMonth() - 1, 1), to: new Date(n.getFullYear(), n.getMonth(), 0) }; } },
    { label: 'Last 30 days', fn: () => { const t = new Date(); const f = new Date(t); f.setDate(t.getDate() - 29); return { from: f, to: t }; } },
    { label: 'Last 90 days', fn: () => { const t = new Date(); const f = new Date(t); f.setDate(t.getDate() - 89); return { from: f, to: t }; } },
    { label: 'Full FY',      fn: () => ({ from: fyStart, to: fyEnd }) },
  ], [fyStart, fyEnd]);

  const applyPreset = (p) => {
    const r    = p.fn();
    const from = r.from < fyStart ? fyStart : r.from;
    const to   = r.to   > fyEnd   ? fyEnd   : r.to;
    apply({ from, to });
  };

  const labelFrom = confirmed.from?.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  const labelTo   = confirmed.to?.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' });

  return (
    <div className="relative">
      {/* Trigger */}
      <button
        onClick={() => { setPickerRange(confirmed); setOpen(o => !o); }}
        className={cn(
          'flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-lg transition-all font-medium',
          confirmed.from
            ? 'bg-primary-50 text-primary-700 ring-1 ring-inset ring-primary-200'
            : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
        )}
      >
        <CalendarDays className="w-3.5 h-3.5" />
        {confirmed.from
          ? confirmed.to ? `${labelFrom} – ${labelTo}` : labelFrom
          : 'Date range'}
        <ChevronDown className="w-3 h-3 opacity-50" />
        {confirmed.from && (
          <span
            onClick={e => { e.stopPropagation(); apply({ from: null, to: null }); }}
            className="ml-0.5 text-primary-400 hover:text-primary-700"
          >
            <X className="w-3 h-3" />
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={close} />
            <motion.div
              initial={{ opacity: 0, y: -6, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.97 }}
              transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="absolute left-0 top-full mt-2 z-50 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-slate-100">
                <div>
                  <p className="text-sm font-semibold text-slate-800">Select date range</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {!pickerRange.from
                      ? 'Click a start date'
                      : !pickerRange.to
                        ? `From ${pickerRange.from.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} — now click end date`
                        : `${pickerRange.from.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })} → ${pickerRange.to.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}`}
                  </p>
                </div>
                <button onClick={close} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Presets */}
              <div className="flex items-center gap-1.5 px-5 py-2.5 border-b border-slate-100">
                {PRESETS.map(p => (
                  <button key={p.label} onClick={() => applyPreset(p)}
                    className="px-2.5 py-1 text-[11px] font-medium rounded-full border border-slate-200 text-slate-500 hover:border-indigo-300 hover:text-primary-600 hover:bg-primary-50 transition-all">
                    {p.label}
                  </button>
                ))}
              </div>

              {/* Calendar — state changes here are isolated to this component */}
              <Calendar
                mode="range"
                numberOfMonths={2}
                selected={pickerRange}
                onSelect={(range) => {
                  setPickerRange(range ?? { from: null, to: null });
                }}
                defaultMonth={fyStart}
                startMonth={fyStart}
                endMonth={fyEnd}
                className="p-4"
              />

              {/* Footer */}
              <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 bg-slate-50/60">
                <span className="text-xs text-slate-400">
                  {pickerRange.from && pickerRange.to
                    ? `${visibleCount} month${visibleCount !== 1 ? 's' : ''} selected`
                    : pickerRange.from ? 'Now select end date' : 'No range selected'}
                </span>
                <div className="flex items-center gap-2">
                  {pickerRange.from && (
                    <button onClick={() => { setPickerRange({ from: null, to: null }); apply({ from: null, to: null }); }}
                      className="text-xs text-slate-400 hover:text-red-500 transition-colors px-2 py-1">
                      Clear
                    </button>
                  )}
                  {pickerRange.from && pickerRange.to && (
                    <button onClick={() => apply(pickerRange)}
                      className="text-xs font-medium bg-primary-500 text-white px-3 py-1.5 rounded-lg hover:bg-primary-600 transition-colors">
                      Apply
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}


async function fetchRawData(fiscalYear = 2027, dateFrom = null, dateTo = null) {
  const summaryRes = await actualsAPI.getChannels(fiscalYear, dateFrom, dateTo);
  const channels   = summaryRes.data;
  if (!channels?.length) return EMPTY_RAW;

  const knownKeys  = new Set(LEAF_CHANNELS.map(c => c.value));
  const byChannel  = {};
  const channelMeta = {};

  for (const ch of channels) {
    byChannel[ch.channel] = {};
    channelMeta[ch.channel] = {
      display: ch.channel_display || ch.channel,
      isKnown: knownKeys.has(ch.channel),
    };
    for (const li of ch.totals.line_items) {
      if (li.type === 'percent') continue;
      byChannel[ch.channel][li.key] = toRupees(li.monthly);
    }
  }

  // Compute "myntra" virtual aggregate = msor + mnow + sjit + ppmp
  byChannel['myntra'] = {};
  channelMeta['myntra'] = { display: 'Myntra', isKnown: true };
  for (const sub of MYNTRA_SUBS) {
    if (!byChannel[sub]) continue;
    for (const [key, vals] of Object.entries(byChannel[sub])) {
      byChannel['myntra'][key] = sumArrays(byChannel['myntra'][key] || Array(12).fill(0), vals);
    }
  }

  // overall = sum of all REAL leaf channels (skip virtual 'myntra' to avoid double-counting)
  const overall = {};
  for (const [chKey, chData] of Object.entries(byChannel)) {
    if (chKey === 'myntra') continue;
    for (const [key, monthly] of Object.entries(chData)) {
      overall[key] = sumArrays(overall[key] || Array(12).fill(0), monthly);
    }
  }

  // Fetch per-category details
  const details      = await Promise.all(channels.map(ch => actualsAPI.getChannelDetail(ch.channel, fiscalYear, dateFrom, dateTo).catch(() => null)));
  const byCategory   = {};
  const byChanCat    = {};
  const categoryMeta = {};

  for (const res of details) {
    if (!res?.data?.categories) continue;
    const chId = res.data.channel;
    for (const cat of res.data.categories) {
      const catId = CAT_NAME_TO_ID[cat.name] || slugify(cat.name);
      if (!catId) continue;
      categoryMeta[catId] = cat.name;
      const ccKey = `${chId}_${catId}`;
      byChanCat[ccKey] = {};
      for (const li of cat.line_items) {
        if (li.type === 'percent') continue;
        const monthly = toRupees(li.monthly);
        byCategory[catId] = byCategory[catId] || {};
        byCategory[catId][li.key] = sumArrays(byCategory[catId][li.key] || Array(12).fill(0), monthly);
        byChanCat[ccKey][li.key] = monthly;
      }
    }
    // Also aggregate Myntra byChanCat so category filter works on the virtual Myntra channel
    if (MYNTRA_SUBS.includes(res.data.channel)) {
      for (const cat of res.data.categories) {
        const catId = CAT_NAME_TO_ID[cat.name] || slugify(cat.name);
        if (!catId) continue;
        const myntraCatKey = `myntra_${catId}`;
        byChanCat[myntraCatKey] = byChanCat[myntraCatKey] || {};
        for (const li of cat.line_items) {
          if (li.type === 'percent') continue;
          const monthly = toRupees(li.monthly);
          byChanCat[myntraCatKey][li.key] = sumArrays(byChanCat[myntraCatKey][li.key] || Array(12).fill(0), monthly);
        }
      }
    }
  }

  return { overall, byChannel, byChannel_real: byChannel, byCategory, byChanCat, channelMeta, categoryMeta };
}

function getEffective12(rawData, apiKey, selectedChannels, selectedCategories) {
  const isCat  = !selectedCategories.includes('all');
  const isChan = !selectedChannels.includes('all');
  if (isChan && isCat) {
    return sumArrays(...selectedChannels.flatMap(chan => selectedCategories.map(cat => {
      const leafIds = PARENT_CHILDREN[chan] || [chan];
      const fromSubs = sumArrays(...leafIds.map(ch => rawData.byChanCat[`${ch}_${cat}`]?.[apiKey] || Array(12).fill(0)));
      // Fallback: if no sub-channel data exists (e.g. AOP has parent channel directly), use parent
      if (fromSubs.every(v => v === 0) && PARENT_CHILDREN[chan]) {
        return rawData.byChanCat[`${chan}_${cat}`]?.[apiKey] || Array(12).fill(0);
      }
      return fromSubs;
    })));
  }
  if (isChan) {
    return sumArrays(...selectedChannels.map(chan => {
      const leafIds = PARENT_CHILDREN[chan] || [chan];
      // Check if any sub-channel has data; if not, fall back to the parent channel key directly
      const hasSubData = leafIds.some(ch => rawData.byChannel[ch]);
      if (!hasSubData && PARENT_CHILDREN[chan]) {
        return rawData.byChannel[chan]?.[apiKey] || Array(12).fill(0);
      }
      return sumArrays(...leafIds.map(ch => rawData.byChannel[ch]?.[apiKey] || Array(12).fill(0)));
    }));
  }
  if (isCat) return sumArrays(...selectedCategories.map(cat => rawData.byCategory[cat]?.[apiKey] || Array(12).fill(0)));
  return rawData.overall[apiKey] || Array(12).fill(0);
}

// ─── Main component ────────────────────────────────────────────────────────────

export default function ActualsPage() {
  const [loading,          setLoading]          = useState(true);
  const [rawData,          setRawData]          = useState(EMPTY_RAW);
  const [aopRawData,       setAopRawData]       = useState(EMPTY_RAW);
  const [viewData,         setViewData]         = useState({});   // computed from rawData
  const [aopViewData,      setAopViewData]      = useState({});   // computed from aopRawData
  const [timePeriod,       setTimePeriod]       = useState('monthly');
  const [selectedChannels,  setSelectedChannels]  = useState(['all']);
  const [selectedCategories, setSelectedCategories] = useState(['all']);
  const [expandedRows,     setExpandedRows]     = useState(new Set());
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(2027);
  const [availableFYs,     setAvailableFYs]     = useState([2027]);
  const [showAOPComparison, setShowAOPComparison] = useState(false);
  const [showPct,           setShowPct]           = useState(false);
  const [hasData,          setHasData]          = useState(false);
  const [dateRange,         setDateRange]         = useState({ from: null, to: null }); // confirmed range → triggers API

  // Import dialog
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile,       setImportFile]       = useState(null);
  const [importing,        setImporting]        = useState(false);
  const importRef = useRef(null);

  const { format: numFmt } = useNumberFormat();
  const fmt = useCallback(v => applyFormat(v, numFmt), [numFmt]);

  const fyMonths = useMemo(() => getFYMonths(selectedFiscalYear), [selectedFiscalYear]);
  const fyShort  = String(selectedFiscalYear % 100).padStart(2, '0');
  const periodConfig = useMemo(() => {
    const base = TIME_PERIODS[timePeriod];
    return {
      ...base,
      format: (i) => {
        if (timePeriod === 'monthly')     return fyMonths[i];
        if (timePeriod === 'quarterly')   return `Q${i+1} FY${fyShort}`;
        if (timePeriod === 'half_yearly') return i === 0 ? `H1 FY${fyShort}` : `H2 FY${fyShort}`;
        return `FY${selectedFiscalYear}`;
      },
    };
  }, [timePeriod, fyMonths, fyShort, selectedFiscalYear]);

  // fyMonthStart[i] = first day of fiscal month i (Apr-26 = i=0)
  const fyMonthStart = useMemo(() =>
    fyMonths.map((_, i) => {
      const calMonth = (3 + i) % 12; // Apr=3, May=4, ..., Mar=2
      const year     = calMonth >= 3 ? selectedFiscalYear - 1 : selectedFiscalYear;
      return new Date(year, calMonth, 1);
    }),
  [fyMonths, selectedFiscalYear]);

  const visiblePeriods = useMemo(() => {
    const total = periodConfig.periods;
    // Only filter when BOTH dates are selected — never on partial selection
    if (timePeriod !== 'monthly' || !dateRange.from || !dateRange.to) {
      return Array.from({ length: total }, (_, i) => i);
    }
    const { from, to } = dateRange;
    return Array.from({ length: total }, (_, i) => i).filter(i => {
      const mStart = fyMonthStart[i];
      const mEnd   = new Date(mStart.getFullYear(), mStart.getMonth() + 1, 0);
      return mStart <= to && mEnd >= from;
    });
  }, [timePeriod, periodConfig.periods, dateRange, fyMonthStart]);

  const clearMonthRange = () => { setDateRange({ from: null, to: null }); };

  // Dynamic channel/category options from DB data
  const dynamicChannelOptions = useMemo(() => {
    const knownKeys = new Set(LEAF_CHANNELS.map(c => c.value));
    const newCh = Object.keys(rawData.channelMeta || {})
      .filter(k => !knownKeys.has(k) && k !== 'myntra')
      .map(k => ({ value: k, label: rawData.channelMeta[k]?.display || k, isChild: true }));
    return newCh.length ? [...CHANNEL_OPTIONS, { value:'_new', label:'New Channels', isParent: true }, ...newCh] : CHANNEL_OPTIONS;
  }, [rawData.channelMeta]);

  const dynamicCategoryOptions = useMemo(() => {
    const knownIds = new Set(CATEGORY_OPTIONS.filter(c => c.value !== 'all').map(c => c.value));
    const newCats  = Object.entries(rawData.categoryMeta || {})
      .filter(([id]) => !knownIds.has(id))
      .map(([id, name]) => ({ value: id, label: name }));
    return newCats.length ? [...CATEGORY_OPTIONS, ...newCats] : CATEGORY_OPTIONS;
  }, [rawData.categoryMeta]);

  const dynamicLeafChannels = useMemo(() => {
    const knownKeys = new Set(LEAF_CHANNELS.map(c => c.value));
    const newLeafs  = Object.entries(rawData.channelMeta || {})
      .filter(([k]) => !knownKeys.has(k) && k !== 'myntra')
      .map(([k, meta]) => ({ value: k, label: meta?.display || k, isChild: true }));
    return [...LEAF_CHANNELS, ...newLeafs];
  }, [rawData.channelMeta]);

  // ── Load actuals data ─────────────────────────────────────────────────────────

  const loadData = useCallback(async (fy, dateFrom = null, dateTo = null) => {
    try {
      const [data, fyRes, ohRes] = await Promise.all([
        fetchRawData(fy, dateFrom, dateTo),
        actualsAPI.getFiscalYears().catch(() => null),
        actualsAPI.getOverhead(fy, dateFrom, dateTo).catch(() => null),
      ]);
      setRawData(data);
      const hasAny = Object.keys(data.overall).length > 0;
      setHasData(hasAny);

      if (fyRes?.data?.length) {
        const years = Array.from(new Set([...fyRes.data, fy])).sort();
        setAvailableFYs(years);
      }

      // Build overhead viewData from OH collection
      if (ohRes?.data?.data) {
        const ohData = ohRes.data.data;
        const ohRupees = {};
        for (const [key, monthly12] of Object.entries(ohData)) {
          ohRupees[key] = Array(12).fill(0).map((_, i) => round2((monthly12[i] || 0) * TO_RUPEES));
        }
        setViewData(prev => ({ ...prev, _overhead: ohRupees }));
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load actuals data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (dateRange.from && !dateRange.to) return; // wait for end date before reloading
    loadData(selectedFiscalYear, dateRange.from ?? null, dateRange.to ?? null);
  }, [selectedFiscalYear, dateRange.from, dateRange.to]); // eslint-disable-line

  // ── Load AOP data for comparison ──────────────────────────────────────────────

  useEffect(() => {
    if (!showAOPComparison) return;
    Promise.all([
      aopAPI.getChannels(selectedFiscalYear).catch(() => null),
      aopAPI.getOverhead(selectedFiscalYear).catch(() => null),
    ]).then(async ([chanRes, ohRes]) => {
      if (!chanRes?.data?.length) return;

      const byChannel = {};
      for (const ch of chanRes.data) {
        byChannel[ch.channel] = {};
        for (const li of ch.totals.line_items) {
          if (li.type === 'percent') continue;
          byChannel[ch.channel][li.key] = toRupees(li.monthly);
        }
      }

      const overall = {};
      for (const [chKey, chData] of Object.entries(byChannel)) {
        for (const [key, monthly] of Object.entries(chData)) {
          overall[key] = sumArrays(overall[key] || Array(12).fill(0), monthly);
        }
      }

      // Fetch per-channel details so category filter works on AOP comparison
      const details = await Promise.all(
        chanRes.data.map(ch => aopAPI.getChannelDetail(ch.channel, selectedFiscalYear).catch(() => null))
      );
      const byCategory = {};
      const byChanCat  = {};
      const categoryMeta = {};
      for (const res of details) {
        if (!res?.data?.categories) continue;
        const chId = res.data.channel;
        for (const cat of res.data.categories) {
          const catId = CAT_NAME_TO_ID[cat.name] || slugify(cat.name);
          if (!catId) continue;
          categoryMeta[catId] = cat.name;
          const ccKey = `${chId}_${catId}`;
          byChanCat[ccKey] = {};
          for (const li of cat.line_items) {
            if (li.type === 'percent') continue;
            const monthly = toRupees(li.monthly);
            byCategory[catId] = byCategory[catId] || {};
            byCategory[catId][li.key] = sumArrays(byCategory[catId][li.key] || Array(12).fill(0), monthly);
            byChanCat[ccKey][li.key] = monthly;
          }
        }
      }

      let ohRupees = {};
      if (ohRes?.data?.data) {
        for (const [key, monthly12] of Object.entries(ohRes.data.data)) {
          ohRupees[key] = Array(12).fill(0).map((_,i) => round2((monthly12[i]||0)*TO_RUPEES));
        }
      }

      setAopRawData({ overall, byChannel, byCategory, byChanCat, channelMeta:{}, categoryMeta, _overhead: ohRupees });
    });
  }, [showAOPComparison, selectedFiscalYear]);

  // ── Build display data from rawData + filters ─────────────────────────────────

  const buildDisplayData = useCallback((raw, ohData, periods) => {
    if (!raw || Object.keys(raw.overall || {}).length === 0) return {};
    const agg = (apiKey, negate = false) => {
      const m12 = getEffective12(raw, apiKey, selectedChannels, selectedCategories);
      const vals = negate ? m12.map(absVal) : m12;
      return aggregatePeriods(vals, periods);
    };

    const ed = {};
    // Revenue / cost rows from channel data
    ed.gross_revenue_incl_gst = agg('gross_sales');
    ed.less_discounts         = agg('discount',        true);
    ed.less_cancellations     = agg('cancellations',   true);
    ed.less_rto               = agg('rto',             true);
    ed.less_returns           = agg('returns',         true);
    ed.less_discrepancy       = agg('discrepancy',     true);
    ed.cogs_product           = agg('material_cost',   true);
    ed.cogs_packaging         = agg('packing',         true);
    ed.payment_gateway        = agg('payment_gateway', true);
    ed.logistics_shipping     = agg('logistics',       true);
    ed.channel_margin         = agg('channel_margins', true);

    // Channel breakdown keys
    for (const ch of dynamicLeafChannels) {
      for (const [rowId, apiKey] of Object.entries(ROW_TO_API)) {
        const m12  = raw.byChannel[ch.value]?.[apiKey] || Array(12).fill(0);
        const vals = NEGATIVE_KEYS.has(apiKey) ? m12.map(absVal) : m12;
        ed[`${rowId}__ch__${ch.value}`] = aggregatePeriods(vals, periods);
      }
    }
    // Myntra virtual channel breakdown (aggregate of sub-channels)
    for (const [rowId, apiKey] of Object.entries(ROW_TO_API)) {
      const m12  = raw.byChannel['myntra']?.[apiKey] || Array(12).fill(0);
      const vals = NEGATIVE_KEYS.has(apiKey) ? m12.map(absVal) : m12;
      ed[`${rowId}__ch__myntra`] = aggregatePeriods(vals, periods);
    }
    // Category breakdown keys
    for (const cat of dynamicCategoryOptions.filter(c => c.value !== 'all')) {
      for (const [rowId, apiKey] of Object.entries(ROW_TO_API)) {
        const m12 = !selectedChannels.includes('all')
          ? sumArrays(...selectedChannels.flatMap(chan => (PARENT_CHILDREN[chan]||[chan]).map(ch => raw.byChanCat?.[`${ch}_${cat.value}`]?.[apiKey] || Array(12).fill(0))))
          : raw.byCategory?.[cat.value]?.[apiKey] || Array(12).fill(0);
        const vals = NEGATIVE_KEYS.has(apiKey) ? m12.map(absVal) : m12;
        ed[`${rowId}__cat__${cat.value}`] = aggregatePeriods(vals, periods);
      }
    }

    // Overhead rows — read from OH collection (flat, always 12 months, aggregate if needed)
    const oh = ohData || {};
    const ohAgg = (key) => aggregatePeriods(oh[key] || Array(12).fill(0), periods);
    const ZERO  = () => Array(periods).fill(0);

    // Map each ad-spend row to the leaf channel(s) it belongs to
    const AD_CHANNEL_MAP = {
      fb_spend:        ['d2c', 'website'],
      meta_spend:      ['d2c', 'website'],
      google_spend:    ['d2c', 'website'],
      affiliate_spend: ['d2c', 'website'],
      retention_spend: ['d2c', 'website'],
      amazon_ads:      ['amazon'],
      flipkart_ads:    ['flipkart'],
      myntra_ads:      ['msor', 'mnow', 'sjit', 'ppmp'],
      eternz_ads:      ['eternz'],
      meesho_ads:      ['meesho'],
      nykaa_ads:       ['nykaa'],
      ajio_ads:        ['ajio'],
      blinkit_ads:     ['blinkit'],
      zepto_ads:       ['zepto'],
      knot_ads:        ['knot'],
      slikk_ads:       ['slikk'],
      swiggy_ads:      ['swiggy'],
      zilo_ads:        ['zilo'],
    };

    // Helper: show ad spend only when the selected channel includes that channel's leaf
    const adOhAgg = (key) => {
      if (selectedChannels.includes('all')) return ohAgg(key);
      const adLeafs = AD_CHANNEL_MAP[key] || [];
      const selLeafs = selectedChannels.flatMap(chan => PARENT_CHILDREN[chan] || [chan]);
      const visible = adLeafs.some(l => selLeafs.includes(l));
      return visible ? ohAgg(key) : ZERO();
    };

    ed.fb_spend         = adOhAgg('fb_spend');
    ed.meta_spend       = adOhAgg('meta_spend');
    ed.google_spend     = adOhAgg('google_spend');
    ed.affiliate_spend  = adOhAgg('affiliate_spend');
    ed.retention_spend  = adOhAgg('retention_spend');
    ed.amazon_ads       = adOhAgg('amazon_ads');
    ed.flipkart_ads     = adOhAgg('flipkart_ads');
    ed.myntra_ads       = adOhAgg('myntra_ads');
    ed.eternz_ads       = adOhAgg('eternz_ads');
    ed.meesho_ads       = adOhAgg('meesho_ads');
    ed.nykaa_ads        = adOhAgg('nykaa_ads');
    ed.ajio_ads         = adOhAgg('ajio_ads');
    ed.blinkit_ads      = adOhAgg('blinkit_ads');
    ed.zepto_ads        = adOhAgg('zepto_ads');
    ed.knot_ads         = adOhAgg('knot_ads');
    ed.slikk_ads        = adOhAgg('slikk_ads');
    ed.swiggy_ads       = adOhAgg('swiggy_ads');
    ed.zilo_ads         = adOhAgg('zilo_ads');
    // Brand & Marketing
    ed.content_influencer   = ohAgg('content_influencer');
    ed.brand_identity       = ohAgg('brand_identity');
    ed.branding_content     = ohAgg('branding_content');
    ed.seo                  = ohAgg('seo');
    ed.pr_agency            = ohAgg('pr_agency');
    ed.influencer_marketing = ohAgg('influencer_marketing');
    ed.photography          = ohAgg('photography');
    ed.inhouse_shoots       = ohAgg('inhouse_shoots');
    ed.brand_activation     = ohAgg('brand_activation');
    ed.brand_film           = ohAgg('brand_film');
    ed.brand_other          = ohAgg('brand_other');
    // Corporate Overheads
    ed.employee_cost          = ohAgg('employee_cost');
    ed.website_tools          = ohAgg('website_tools');
    ed.ops_tools              = ohAgg('ops_tools');
    ed.customer_support_tools = ohAgg('customer_support_tools');
    ed.website_dev            = ohAgg('website_dev');
    ed.data_tools             = ohAgg('data_tools');
    ed.other_tools            = ohAgg('other_tools');
    ed.retention_tools        = ohAgg('retention_tools');
    ed.retention_spends       = ohAgg('retention_spends');
    ed.staff_welfare          = ohAgg('staff_welfare');
    ed.professional_expenses  = ohAgg('professional_expenses');
    ed.office_exp             = ohAgg('office_exp');
    ed.warehouse_exp          = ohAgg('warehouse_exp');
    ed.rental_expenses        = ohAgg('rental_expenses');
    ed.travel_conveyance      = ohAgg('travel_conveyance');
    ed.damaged_goods          = ohAgg('damaged_goods');

    // Preserve _overhead so filter-change rebuilds don't lose overhead data
    ed._overhead = ohData || {};

    return ed;
  }, [selectedChannels, selectedCategories, dynamicLeafChannels, dynamicCategoryOptions]);

  // Rebuild viewData when filters/period changes
  useEffect(() => {
    const periods = periodConfig.periods;
    const oh = viewData._overhead || {};
    setViewData(buildDisplayData(rawData, oh, periods));
  }, [rawData, selectedChannels, selectedCategories, timePeriod, selectedFiscalYear]); // eslint-disable-line

  useEffect(() => {
    if (!showAOPComparison || !aopRawData.overall) return;
    const periods = periodConfig.periods;
    const oh = aopRawData._overhead || {};
    setAopViewData(buildDisplayData(aopRawData, oh, periods));
  }, [aopRawData, showAOPComparison, selectedChannels, selectedCategories, timePeriod]); // eslint-disable-line

  // ── Computed P&L (formula fields) ─────────────────────────────────────────────

  const computePL = useCallback((ed, periods) => {
    if (!ed || Object.keys(ed).length === 0) return {};
    const get = id => ed[id] || Array(periods).fill(0);

    const gs   = get('gross_revenue_incl_gst');
    const disc = get('less_discounts');
    const canc = get('less_cancellations');
    const rto  = get('less_rto');
    const ret  = get('less_returns');
    const dsc2 = get('less_discrepancy');
    const matc = get('cogs_product');
    const pack = get('cogs_packaging');
    const pg   = get('payment_gateway');
    const log  = get('logistics_shipping');
    const chan = get('channel_margin');

    const discountsBlock   = disc;
    const totalRev         = gs.map((v,i) => round2(v - disc[i]));
    const adjustmentsBlock = canc.map((v,i) => round2(v + rto[i] + ret[i] + dsc2[i]));
    const netRev           = totalRev.map((v,i) => round2(v - adjustmentsBlock[i]));
    const cogs             = matc.map((v,i) => round2(v + pack[i]));
    const gm               = netRev.map((v,i) => round2(v - cogs[i]));
    const directCosts      = pg.map((v,i) => round2(v + log[i] + chan[i]));
    const cm1              = gm.map((v,i) => round2(v - directCosts[i]));

    const mktAds  = get('amazon_ads').map((v,i) => round2(v + get('flipkart_ads')[i] + get('myntra_ads')[i] + get('eternz_ads')[i] + get('meesho_ads')[i] + get('nykaa_ads')[i] + get('ajio_ads')[i]));
    const qcAds   = get('blinkit_ads').map((v,i) => round2(v + get('zepto_ads')[i] + get('knot_ads')[i] + get('slikk_ads')[i] + get('swiggy_ads')[i] + get('zilo_ads')[i]));
    const d2cTotal = get('meta_spend').map((v,i) => round2(v + get('google_spend')[i] + get('affiliate_spend')[i] + get('retention_spend')[i]));
    const perfMkt  = d2cTotal.map((v,i) => round2(v + mktAds[i] + qcAds[i]));
    const cm2     = cm1.map((v,i) => round2(v - perfMkt[i]));

    const brandMkt = get('content_influencer').map((v,i) => round2(v + get('brand_identity')[i] + get('branding_content')[i] + get('seo')[i] + get('pr_agency')[i] + get('influencer_marketing')[i] + get('photography')[i] + get('inhouse_shoots')[i] + get('brand_activation')[i] + get('brand_film')[i] + get('brand_other')[i]));
    const tech     = get('website_tools').map((v,i) => round2(v + get('ops_tools')[i] + get('customer_support_tools')[i] + get('website_dev')[i] + get('data_tools')[i] + get('other_tools')[i] + get('retention_tools')[i] + get('retention_spends')[i]));
    const oh       = get('employee_cost').map((v,i) => round2(v + brandMkt[i] + tech[i] + get('staff_welfare')[i] + get('professional_expenses')[i] + get('office_exp')[i] + get('warehouse_exp')[i] + get('rental_expenses')[i] + get('travel_conveyance')[i] + get('damaged_goods')[i]));
    const ebit     = cm2.map((v,i) => round2(v - oh[i]));

    return {
      ...ed,
      discounts_block: discountsBlock, total_revenue_incl_gst: totalRev,
      adjustments_block: adjustmentsBlock, net_revenue: netRev,
      cogs, gross_margin: gm, direct_costs: directCosts, cm1,
      marketplace_marketing: mktAds, qc_marketing: qcAds,
      fb_spend: d2cTotal,
      direct_marketing_section: perfMkt, performance_marketing: perfMkt,
      brand_marketing: brandMkt, cm2,
      tech_expenses: tech, total_overheads: oh, corporate_overheads_section: oh, ebitda: ebit,
    };
  }, []);

  const computedActuals = useMemo(() => computePL(viewData, periodConfig.periods), [viewData, periodConfig.periods, computePL]);
  const computedAOP     = useMemo(() => computePL(aopViewData, periodConfig.periods), [aopViewData, periodConfig.periods, computePL]);

  // ── Helpers ───────────────────────────────────────────────────────────────────

  const getVal   = useCallback((id, i) => computedActuals[id]?.[i] ?? 0, [computedActuals]);
  const getTotal = useCallback((id) => (computedActuals[id] || []).reduce((s,v) => s+v, 0), [computedActuals]);
  const getAOPVal   = useCallback((id, i) => computedAOP[id]?.[i] ?? 0, [computedAOP]);
  const getAOPTotal = useCallback((id) => (computedAOP[id] || []).reduce((s,v) => s+v, 0), [computedAOP]);

  const PCT_BASE = {
    discounts_block: 'gross_revenue_incl_gst', less_discounts: 'gross_revenue_incl_gst',
    total_revenue_incl_gst: 'gross_revenue_incl_gst',
    adjustments_block: 'total_revenue_incl_gst', less_cancellations: 'total_revenue_incl_gst',
    less_rto: 'total_revenue_incl_gst', less_returns: 'total_revenue_incl_gst',
    less_discrepancy: 'total_revenue_incl_gst', net_revenue: 'total_revenue_incl_gst',
    cogs: 'net_revenue', cogs_product: 'net_revenue', cogs_packaging: 'net_revenue',
    gross_margin: 'net_revenue',
    direct_costs: 'net_revenue', payment_gateway: 'net_revenue',
    logistics_shipping: 'net_revenue', channel_margin: 'net_revenue',
    cm1: 'net_revenue',
    direct_marketing_section: 'net_revenue',
    fb_spend: 'net_revenue', meta_spend: 'net_revenue', google_spend: 'net_revenue',
    affiliate_spend: 'net_revenue', retention_spend: 'net_revenue', marketplace_marketing: 'net_revenue',
    amazon_ads: 'net_revenue', flipkart_ads: 'net_revenue', myntra_ads: 'net_revenue',
    eternz_ads: 'net_revenue', meesho_ads: 'net_revenue', nykaa_ads: 'net_revenue',
    ajio_ads: 'net_revenue', qc_marketing: 'net_revenue',
    blinkit_ads: 'net_revenue', zepto_ads: 'net_revenue', knot_ads: 'net_revenue',
    slikk_ads: 'net_revenue', swiggy_ads: 'net_revenue', zilo_ads: 'net_revenue',
    cm2: 'net_revenue',
    corporate_overheads_section: 'net_revenue', employee_cost: 'net_revenue',
    brand_marketing: 'net_revenue', content_influencer: 'net_revenue',
    brand_identity: 'net_revenue', branding_content: 'net_revenue', seo: 'net_revenue',
    pr_agency: 'net_revenue', influencer_marketing: 'net_revenue',
    photography: 'net_revenue', inhouse_shoots: 'net_revenue',
    brand_activation: 'net_revenue', brand_film: 'net_revenue', brand_other: 'net_revenue',
    tech_expenses: 'net_revenue', website_tools: 'net_revenue', ops_tools: 'net_revenue',
    customer_support_tools: 'net_revenue', website_dev: 'net_revenue',
    data_tools: 'net_revenue', other_tools: 'net_revenue', retention_tools: 'net_revenue',
    retention_spends: 'net_revenue',
    staff_welfare: 'net_revenue', professional_expenses: 'net_revenue',
    office_exp: 'net_revenue', warehouse_exp: 'net_revenue', rental_expenses: 'net_revenue',
    travel_conveyance: 'net_revenue', damaged_goods: 'net_revenue',
    ebitda: 'net_revenue',
  };

  const PCT_COLOR = {
    discounts_block: 'text-amber-600', less_discounts: 'text-amber-600',
    total_revenue_incl_gst: 'text-sky-600',
    adjustments_block: 'text-orange-500', less_cancellations: 'text-orange-500',
    less_rto: 'text-orange-500', less_returns: 'text-orange-500',
    less_discrepancy: 'text-orange-500',
    net_revenue: 'text-sky-600',
    gross_margin: 'text-emerald-600',
    cm1: 'text-emerald-600', cm2: 'text-primary-500', ebitda: 'text-primary-500',
  };

  // [bgClass, textClass] for the % pill badge
  const PCT_PILL = {
    discounts_block:    ['bg-amber-50 ring-1 ring-inset ring-amber-200',   'text-amber-700'],
    less_discounts:     ['bg-amber-50 ring-1 ring-inset ring-amber-200',   'text-amber-700'],
    total_revenue_incl_gst: ['bg-sky-50 ring-1 ring-inset ring-sky-200',  'text-sky-700'],
    adjustments_block:  ['bg-orange-50 ring-1 ring-inset ring-orange-200', 'text-orange-600'],
    less_cancellations: ['bg-orange-50 ring-1 ring-inset ring-orange-200', 'text-orange-600'],
    less_rto:           ['bg-orange-50 ring-1 ring-inset ring-orange-200', 'text-orange-600'],
    less_returns:       ['bg-orange-50 ring-1 ring-inset ring-orange-200', 'text-orange-600'],
    less_discrepancy:   ['bg-orange-50 ring-1 ring-inset ring-orange-200', 'text-orange-600'],
    net_revenue:        ['bg-sky-50 ring-1 ring-inset ring-sky-200',       'text-sky-700'],
    gross_margin:       ['bg-emerald-50 ring-1 ring-inset ring-emerald-200', 'text-emerald-700'],
    cm1:                ['bg-emerald-50 ring-1 ring-inset ring-emerald-200', 'text-emerald-700'],
    cm2:                ['bg-primary-50 ring-1 ring-inset ring-primary-200', 'text-primary-600'],
    ebitda:             ['bg-primary-50 ring-1 ring-inset ring-primary-200', 'text-primary-600'],
  };
  const PCT_PILL_DEFAULT = ['bg-slate-50 ring-1 ring-inset ring-slate-200', 'text-slate-500'];

  const getPct = useCallback((id, i) => {
    const baseId = PCT_BASE[id];
    if (!baseId) return null;
    const base = Math.abs(getVal(baseId, i));
    if (!base) return null;
    return (Math.abs(getVal(id, i)) / base) * 100;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getVal]);

  const getPctTotal = useCallback((id) => {
    const baseId = PCT_BASE[id];
    if (!baseId) return null;
    const base = Math.abs(getTotal(baseId));
    if (!base) return null;
    return (Math.abs(getTotal(id)) / base) * 100;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTotal]);

  const toggleExpand = useCallback(id => setExpandedRows(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  }), []);

  const expandAll = useCallback(() => {
    const all = [];
    const collect = items => items.forEach(item => {
      if (item.expandable || item.children) all.push(item.id);
      if (item.children) collect(item.children);
    });
    collect(PNL_STRUCTURE);
    setExpandedRows(new Set(all));
  }, []);

  const collapseAll = useCallback(() => setExpandedRows(new Set()), []);

  // Variance: Actuals - AOP
  const getVariance = useCallback((actual, plan) => {
    if (plan === 0) return { amount: actual, pct: null };
    const pct = ((actual - plan) / Math.abs(plan)) * 100;
    return { amount: actual - plan, pct };
  }, []);

  // ── Import handler ────────────────────────────────────────────────────────────

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const res = await actualsAPI.importFile(importFile, selectedFiscalYear);
      const { channels_imported, channels_skipped, overhead_updated } = res.data;
      toast.success(`Imported ${channels_imported.length} channels${overhead_updated ? ' + overhead' : ''}`);
      if (channels_skipped?.length) toast.warning(`Skipped unknown sheets: ${channels_skipped.join(', ')}`);
      setShowImportDialog(false);
      setImportFile(null);
      await loadData(selectedFiscalYear);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleTemplateDownload = async () => {
    try {
      const res = await actualsAPI.downloadTemplate();
      const url = URL.createObjectURL(new Blob([res.data]));
      const a   = document.createElement('a');
      a.href = url; a.download = 'actuals_template.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download template');
    }
  };

  // ── Row renderer ──────────────────────────────────────────────────────────────

  const renderRow = (item, depth = 0) => {
    const isExpanded = expandedRows.has(item.id);
    const hasChildren = item.children?.length > 0;
    const isCost     = item.type === 'cost';
    const isSubtotal = item.type === 'subtotal';
    const isDeduction = item.type === 'deduction';
    const isHighlight = item.highlight;
    const isPrimary  = item.primary;
    const periods    = periodConfig.periods;

    const rowCls = cn(
      'group transition-colors',
      isCost    && 'bg-white',
      isSubtotal && !isHighlight && 'bg-slate-50',
      isHighlight && !isPrimary && 'bg-emerald-50/60',
      isPrimary  && 'bg-primary-50',
    );
    const labelCls = cn(
      'px-3 py-1.5 border-b border-slate-100 sticky left-0 z-10 min-w-[260px]',
      isCost     && 'bg-white',
      isSubtotal && !isHighlight && 'bg-slate-50',
      isHighlight && !isPrimary && 'bg-emerald-50/60',
      isPrimary  && 'bg-primary-50',
      !isCost && !isSubtotal && !isHighlight && 'bg-white',
    );

    const numCols = showAOPComparison ? periods * 3 + 3 : periods + 1;

    return (
      <React.Fragment key={item.id}>
        <tr className={rowCls}>
          <td className={labelCls} style={{ paddingLeft: `${depth * 16 + 12}px` }}>
            <div className="flex items-center gap-1.5">
              {(hasChildren || item.hasChannelBreakdown || item.hasCategoryBreakdown) ? (
                <button onClick={() => toggleExpand(item.id)}
                  className="p-0.5 hover:bg-slate-200 rounded transition-colors flex-shrink-0">
                  {isExpanded
                    ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                    : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                </button>
              ) : <span className="w-5 flex-shrink-0" />}
              <span className={cn(
                'text-sm',
                isCost     && 'font-medium text-slate-800',
                isSubtotal && 'font-semibold text-slate-900',
                isHighlight && 'font-bold',
                isPrimary  && 'font-bold text-primary-800',
                isDeduction && 'text-red-600',
              )}>
                {item.name}
              </span>
            </div>
          </td>

          {/* Monthly / period value columns */}
          {visiblePeriods.map((i) => {
            const act = getVal(item.id, i);
            const plan = showAOPComparison ? getAOPVal(item.id, i) : 0;
            const variance = showAOPComparison ? getVariance(act, plan) : null;
            // Cost direction: lower actual = good
            const isCostRow = isDeduction || isCost;
            const isGood = variance ? (isCostRow ? variance.amount <= 0 : variance.amount >= 0) : null;

            if (showAOPComparison) {
              return (
                <td key={i} className="border-b border-slate-100 p-0">
                  <div className="grid grid-cols-3 divide-x divide-slate-100">
                    <div className="px-1.5 py-1.5 text-right font-mono text-xs text-slate-400 bg-slate-50/30">
                      {fmt(plan)}
                    </div>
                    <div className={cn('px-1.5 py-1.5 text-right font-mono text-xs', isSubtotal && 'font-semibold', isPrimary && 'text-primary-800')}>
                      {fmt(act)}
                    </div>
                    <div className={cn('px-1.5 py-1.5 text-right font-mono text-[10px]',
                      variance?.pct !== null
                        ? (isGood ? 'text-emerald-600 bg-emerald-50/30' : 'text-red-500 bg-red-50/30')
                        : 'text-slate-400'
                    )}>
                      {variance?.pct !== null
                        ? `${variance.pct >= 0 ? '+' : ''}${variance.pct.toFixed(0)}%`
                        : '—'}
                    </div>
                  </div>
                </td>
              );
            }

            {
              const pct = showPct ? getPct(item.id, i) : null;
              const [, txtCls] = (showPct && pct !== null && (PCT_PILL[item.id] || PCT_PILL_DEFAULT)) || [, null];
              return (
                <td key={i} className={cn(
                  'px-2 py-1.5 border-b border-slate-100',
                  isSubtotal && 'font-semibold',
                )}>
                  <div className="flex items-center justify-end gap-1.5">
                    <span className={cn(
                      'font-mono text-sm tabular-nums',
                      isPrimary && 'text-primary-800',
                      act < 0   && 'text-red-500',
                    )}>
                      {fmt(act)}
                    </span>
                    <AnimatePresence>
                      {showPct && pct !== null && (
                        <motion.span
                          key="pct"
                          className={cn('text-[10px] font-mono font-semibold tabular-nums shrink-0', txtCls)}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          transition={{ duration: 0.15 }}
                        >
                          {pct.toFixed(1)}%
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </div>
                </td>
              );
            }
          })}

          {/* YTD column */}
          {(() => {
            const ytdAct  = getTotal(item.id);
            const ytdPlan = getAOPTotal(item.id);
            const ytdVar  = getVariance(ytdAct, ytdPlan);
            const isCostRow = isDeduction || isCost;
            const ytdGood = ytdVar.pct !== null
              ? (isCostRow ? ytdVar.amount <= 0 : ytdVar.amount >= 0)
              : null;
            const ytdVarCls = ytdGood === true
              ? 'text-emerald-600 bg-emerald-100'
              : ytdGood === false
                ? 'text-red-500 bg-red-100'
                : 'text-slate-400';
            const ytdVarLabel = ytdVar.pct !== null
              ? `${ytdVar.pct >= 0 ? '+' : ''}${ytdVar.pct.toFixed(0)}%`
              : '—';
            const ytdBg = isHighlight ? 'bg-emerald-50/60' : isPrimary ? 'bg-primary-50' : 'bg-slate-50';
            return (
              <td className={cn('border-b border-slate-100 border-l border-slate-200 p-0', ytdBg)}>
                {showAOPComparison ? (
                  <div className="grid grid-cols-3 divide-x divide-slate-200">
                    <div className="px-1.5 py-1.5 text-right font-mono text-xs text-slate-500 font-semibold">
                      {fmt(ytdPlan)}
                    </div>
                    <div className={cn('px-1.5 py-1.5 text-right font-mono text-xs font-semibold', isPrimary && 'text-primary-800')}>
                      {fmt(ytdAct)}
                    </div>
                    <div className={cn('px-1.5 py-1.5 text-right font-mono text-[10px] font-semibold', ytdVarCls)}>
                      {ytdVarLabel}
                    </div>
                  </div>
                ) : (() => {
                    const ytdPct = showPct ? getPctTotal(item.id) : null;
                    const [, ytdTxtCls] = (showPct && ytdPct !== null && (PCT_PILL[item.id] || PCT_PILL_DEFAULT)) || [, null];
                    return (
                      <div className="px-2 py-1.5 flex items-center justify-end gap-1.5">
                        <span className={cn('font-mono text-sm font-semibold tabular-nums', isPrimary && 'text-primary-800', ytdAct < 0 && 'text-red-500')}>
                          {fmt(ytdAct)}
                        </span>
                        <AnimatePresence>
                          {showPct && ytdPct !== null && (
                            <motion.span
                              key="ytd-pct"
                              className={cn('text-[10px] font-mono font-semibold tabular-nums shrink-0', ytdTxtCls)}
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.8 }}
                              transition={{ duration: 0.15 }}
                            >
                              {ytdPct.toFixed(1)}%
                            </motion.span>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })()}
              </td>
            );
          })()}
        </tr>

        {/* Channel breakdown — filtered to match channel filter selection */}
        {isExpanded && item.hasChannelBreakdown && (() => {
          // Which leaf channels to show (mirrors AOPPage renderBreakdownRows logic)
          const visibleLeafs = dynamicLeafChannels.filter(c =>
            selectedChannels.includes('all') ||
            selectedChannels.includes(c.value) ||
            selectedChannels.some(chan => (PARENT_CHILDREN[chan] || []).includes(c.value))
          );
          return (
            <>
              <tr className="bg-blue-50/20">
                <td className="px-3 py-1 border-b border-slate-100 sticky left-0 bg-blue-50/20 z-10 text-xs font-semibold text-blue-600"
                  style={{ paddingLeft: `${(depth+1)*16+12}px` }}>
                  <div className="flex items-center gap-1"><Layers className="w-3 h-3" /> By Channel</div>
                </td>
                <td colSpan={numCols - 1} className="border-b border-slate-100" />
              </tr>
              {visibleLeafs.map(ch => {
                const isMyntraSub = MYNTRA_SUBS.includes(ch.value);
                const chAct  = computedActuals[`${item.id}__ch__${ch.value}`]?.reduce((s,v)=>s+v,0) || 0;
                const chPlan = computedAOP[`${item.id}__ch__${ch.value}`]?.reduce((s,v)=>s+v,0) || 0;
                const showMyntraHeader = ch.value === 'msor' && (
                  selectedChannels.includes('all') || selectedChannels.includes('myntra') ||
                  selectedChannels.some(chan => (PARENT_CHILDREN[chan] || []).includes('msor'))
                );
                return (
                  <React.Fragment key={`${item.id}-${ch.value}`}>
                    {showMyntraHeader && (
                      <tr className="bg-primary-50/20">
                        <td className="px-3 py-1 border-b border-slate-50 sticky left-0 bg-primary-50/20 z-10 text-xs font-semibold text-primary-600"
                          style={{ paddingLeft: `${(depth+1)*16+28}px` }}>
                          Myntra (MSOR + MNOW + SJIT + PPMP)
                        </td>
                        {visiblePeriods.map((i) => {
                          const act  = computedActuals[`${item.id}__ch__myntra`]?.[i] || 0;
                          const plan = showAOPComparison ? (computedAOP[`${item.id}__ch__myntra`]?.[i] || 0) : 0;
                          const vPct = plan ? (((act-plan)/Math.abs(plan))*100).toFixed(0) : null;
                          return showAOPComparison ? (
                            <td key={i} className="border-b border-slate-50 p-0">
                              <div className="grid grid-cols-3 divide-x divide-slate-100">
                                <div className="px-1.5 py-1 text-right font-mono text-[10px] text-slate-400">{fmt(plan)}</div>
                                <div className="px-1.5 py-1 text-right font-mono text-[10px] text-primary-700 font-medium">{fmt(act)}</div>
                                <div className={cn('px-1.5 py-1 text-right font-mono text-[9px]', vPct !== null ? (act>=plan?'text-emerald-600':'text-red-500') : 'text-slate-400')}>{vPct !== null ? `${act>=plan?'+':''}${vPct}%` : '—'}</div>
                              </div>
                            </td>
                          ) : <td key={i} className="px-2 py-1 border-b border-slate-50 text-right font-mono text-xs text-primary-700 font-medium">{fmt(act)}</td>;
                        })}
                        <td className="border-b border-slate-50 bg-slate-50 border-l border-slate-200 p-0">
                          {showAOPComparison ? (
                            <div className="grid grid-cols-3 divide-x divide-slate-200">
                              <div className="px-1.5 py-1 text-right font-mono text-[10px] text-slate-500 font-semibold">{fmt(computedAOP[`${item.id}__ch__myntra`]?.reduce((a,b)=>a+b,0)||0)}</div>
                              <div className="px-1.5 py-1 text-right font-mono text-[10px] text-primary-700 font-semibold">{fmt(computedActuals[`${item.id}__ch__myntra`]?.reduce((a,b)=>a+b,0)||0)}</div>
                              <div className="px-1.5 py-1 text-right font-mono text-[9px] text-slate-400">—</div>
                            </div>
                          ) : (
                            <div className="px-2 py-1 text-right font-mono text-xs text-primary-700 font-semibold">{fmt(computedActuals[`${item.id}__ch__myntra`]?.reduce((a,b)=>a+b,0)||0)}</div>
                          )}
                        </td>
                      </tr>
                    )}
                    <tr className="hover:bg-slate-50/20">
                      <td className="px-3 py-1.5 border-b border-slate-50 sticky left-0 bg-white z-10 text-slate-500 text-xs"
                        style={{ paddingLeft: isMyntraSub ? `${(depth+1)*16+44}px` : `${(depth+1)*16+28}px` }}>
                        {isMyntraSub && <span className="text-slate-300 mr-1">↳</span>}
                        {ch.label}
                      </td>
                      {visiblePeriods.map((i) => {
                        const act  = computedActuals[`${item.id}__ch__${ch.value}`]?.[i] || 0;
                        const plan = showAOPComparison ? (computedAOP[`${item.id}__ch__${ch.value}`]?.[i] || 0) : 0;
                        const vPct = showAOPComparison && plan ? (((act-plan)/Math.abs(plan))*100).toFixed(0) : null;
                        return showAOPComparison ? (
                          <td key={i} className="border-b border-slate-50 p-0">
                            <div className="grid grid-cols-3 divide-x divide-slate-100">
                              <div className="px-1.5 py-1 text-right font-mono text-[10px] text-slate-400">{fmt(plan)}</div>
                              <div className="px-1.5 py-1 text-right font-mono text-[10px] text-slate-600">{fmt(act)}</div>
                              <div className={cn('px-1.5 py-1 text-right font-mono text-[9px]', vPct !== null ? (act>=plan?'text-emerald-600':'text-red-500') : 'text-slate-400')}>{vPct !== null ? `${act>=plan?'+':''}${vPct}%` : '—'}</div>
                            </div>
                          </td>
                        ) : (
                          <td key={i} className="px-2 py-1 border-b border-slate-50 text-right font-mono text-xs text-slate-600">{fmt(act)}</td>
                        );
                      })}
                      <td className="border-b border-slate-50 bg-slate-50 border-l border-slate-200 p-0">
                        {showAOPComparison ? (
                          <div className="grid grid-cols-3 divide-x divide-slate-200">
                            <div className="px-1.5 py-1 text-right font-mono text-[10px] text-slate-500 font-semibold">{fmt(chPlan)}</div>
                            <div className="px-1.5 py-1 text-right font-mono text-[10px] text-slate-600 font-semibold">{fmt(chAct)}</div>
                            <div className={cn('px-1.5 py-1 text-right font-mono text-[9px] font-semibold', chPlan ? (chAct>=chPlan?'text-emerald-600':'text-red-500') : 'text-slate-400')}>{chPlan ? `${chAct>=chPlan?'+':''}${(((chAct-chPlan)/Math.abs(chPlan))*100).toFixed(0)}%` : '—'}</div>
                          </div>
                        ) : (
                          <div className="px-2 py-1 text-right font-mono text-xs text-slate-600 font-semibold">{fmt(chAct)}</div>
                        )}
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </>
          );
        })()}

        {/* Category breakdown — filtered to match category filter selection */}
        {isExpanded && item.hasCategoryBreakdown && (() => {
          const visibleCats = dynamicCategoryOptions.filter(c =>
            c.value !== 'all' &&
            (selectedCategories.includes('all') || selectedCategories.includes(c.value))
          );
          return (
            <>
              <tr className="bg-purple-50/20">
                <td className="px-3 py-1 border-b border-slate-100 sticky left-0 bg-purple-50/20 z-10 text-xs font-semibold text-purple-600"
                  style={{ paddingLeft: `${(depth+1)*16+12}px` }}>
                  <div className="flex items-center gap-1"><BarChart3 className="w-3 h-3" /> By Category</div>
                </td>
                <td colSpan={numCols - 1} className="border-b border-slate-100" />
              </tr>
              {visibleCats.map(cat => {
                const catAct  = (computedActuals[`${item.id}__cat__${cat.value}`] || []).reduce((s,v)=>s+v,0);
                const catPlan = (computedAOP[`${item.id}__cat__${cat.value}`] || []).reduce((s,v)=>s+v,0);
                return (
                  <tr key={`${item.id}-${cat.value}`} className="hover:bg-slate-50/20">
                    <td className="px-3 py-1.5 border-b border-slate-50 sticky left-0 bg-white z-10 text-slate-500 text-xs"
                      style={{ paddingLeft: `${(depth+1)*16+28}px` }}>
                      {cat.label}
                    </td>
                    {visiblePeriods.map((i) => {
                      const act  = computedActuals[`${item.id}__cat__${cat.value}`]?.[i] || 0;
                      const plan = showAOPComparison ? (computedAOP[`${item.id}__cat__${cat.value}`]?.[i] || 0) : 0;
                      const vPct = showAOPComparison && plan ? (((act-plan)/Math.abs(plan))*100).toFixed(0) : null;
                      return showAOPComparison ? (
                        <td key={i} className="border-b border-slate-50 p-0">
                          <div className="grid grid-cols-3 divide-x divide-slate-100">
                            <div className="px-1.5 py-1 text-right font-mono text-[10px] text-slate-400">{fmt(plan)}</div>
                            <div className="px-1.5 py-1 text-right font-mono text-[10px] text-slate-600">{fmt(act)}</div>
                            <div className={cn('px-1.5 py-1 text-right font-mono text-[9px]', vPct !== null ? (act>=plan?'text-emerald-600':'text-red-500') : 'text-slate-400')}>{vPct !== null ? `${act>=plan?'+':''}${vPct}%` : '—'}</div>
                          </div>
                        </td>
                      ) : (
                        <td key={i} className="px-2 py-1 border-b border-slate-50 text-right font-mono text-xs text-slate-600">{fmt(act)}</td>
                      );
                    })}
                    <td className="border-b border-slate-50 bg-slate-50 border-l border-slate-200 p-0">
                      {showAOPComparison ? (
                        <div className="grid grid-cols-3 divide-x divide-slate-200">
                          <div className="px-1.5 py-1 text-right font-mono text-[10px] text-slate-500 font-semibold">{fmt(catPlan)}</div>
                          <div className="px-1.5 py-1 text-right font-mono text-[10px] text-slate-600 font-semibold">{fmt(catAct)}</div>
                          <div className={cn('px-1.5 py-1 text-right font-mono text-[9px] font-semibold', catPlan ? (catAct>=catPlan?'text-emerald-600':'text-red-500') : 'text-slate-400')}>{catPlan ? `${catAct>=catPlan?'+':''}${(((catAct-catPlan)/Math.abs(catPlan))*100).toFixed(0)}%` : '—'}</div>
                        </div>
                      ) : (
                        <div className="px-2 py-1 text-right font-mono text-xs text-slate-600 font-semibold">{fmt(catAct)}</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </>
          );
        })()}

        {isExpanded && hasChildren && item.children.map(child => renderRow(child, depth + 1))}
      </React.Fragment>
    );
  };

  // ── Summary KPIs ──────────────────────────────────────────────────────────────

  const netRev   = getTotal('net_revenue');
  const gm       = getTotal('gross_margin');
  const cm1      = getTotal('cm1');
  const cm2      = getTotal('cm2');
  const ebitda   = getTotal('ebitda');
  const gmPct    = netRev ? ((gm / netRev) * 100) : 0;
  const cm2Pct   = netRev ? ((cm2 / netRev) * 100) : 0;
  const ebitdaPct = netRev ? ((ebitda / netRev) * 100) : 0;

  const periods = periodConfig.periods;

  // ── Render ────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-slate-100 rounded animate-pulse w-64" />
        <div className="h-[600px] bg-slate-100 rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Actuals</h2>
          <p className="text-slate-500 text-sm">
            FY {selectedFiscalYear - 1}-{String(selectedFiscalYear % 100).padStart(2,'0')} — Actual P&L
            {!hasData && <span className="ml-2 text-amber-600 font-medium">(No data — import actuals to begin)</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm"
            onClick={() => setShowAOPComparison(v => !v)}
            className={cn(showAOPComparison && 'border-primary-400 bg-primary-50 text-primary-600')}>
            <GitCompare className="w-4 h-4 mr-1.5" />
            {showAOPComparison ? 'Hide AOP Compare' : 'Compare vs AOP'}
          </Button>
          <Button variant="outline" size="sm" onClick={handleTemplateDownload}>
            <Download className="w-4 h-4 mr-1.5" /> Template
          </Button>
          <Button size="sm" onClick={() => setShowImportDialog(true)}>
            <Upload className="w-4 h-4 mr-1.5" /> Import Actuals
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="flex items-stretch border border-slate-200 rounded-xl bg-white divide-x divide-slate-200 overflow-hidden">
        {[
          { label: 'Net Sales',    val: netRev, pct: null,                                  color: 'text-slate-900' },
          { label: 'Gross Margin', val: gm,     pct: gmPct,                                 color: 'text-emerald-700' },
          { label: 'CM1',          val: cm1,    pct: netRev ? cm1/netRev*100 : 0,           color: 'text-emerald-700' },
          { label: 'CM2',          val: cm2,    pct: cm2Pct,                                color: cm2 < 0 ? 'text-red-600' : 'text-primary-600' },
          { label: 'EBITDA',       val: ebitda, pct: ebitdaPct,                             color: ebitda < 0 ? 'text-red-600' : 'text-primary-600' },
        ].map(({ label, val, pct, color }) => (
          <div key={label} className={cn('flex-1 flex items-center gap-2.5 px-4 py-2.5', label === 'EBITDA' && 'bg-primary-50/60')}>
            <div className="min-w-0">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold leading-none mb-0.5">{label}</p>
              <div className="flex items-baseline gap-1.5 flex-wrap">
                <span className={cn('font-mono text-sm font-bold tabular-nums', color)}>{fmt(val)}</span>
                {pct !== null && (
                  <span className={cn('text-[10px] font-mono font-semibold tabular-nums shrink-0', val < 0 ? 'text-red-500' : 'text-emerald-600')}>
                    {pct.toFixed(1)}% NSV
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs: P&L | Cash Flow | Balance Sheet */}
      <Tabs defaultValue="pnl" className="w-full">
        <TabsList>
          <TabsTrigger value="pnl">P&amp;L Statement</TabsTrigger>
          <TabsTrigger value="cashflow">Cash Flow</TabsTrigger>
          <TabsTrigger value="balance">Balance Sheet</TabsTrigger>
        </TabsList>

        {/* ── P&L tab ────────────────────────────────────────────────────── */}
        <TabsContent value="pnl" className="mt-2 space-y-2">
          {/* Filter bar */}
          <div className="flex flex-wrap items-center gap-2">

            {/* Left group: FY + Period + Date Range */}
            <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl px-2 py-1.5 shadow-sm">
              <Select value={String(selectedFiscalYear)} onValueChange={v => { setSelectedFiscalYear(parseInt(v)); clearMonthRange(); }}>
                <SelectTrigger className="w-24 h-7 text-xs border-0 shadow-none bg-transparent focus:ring-0 px-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {availableFYs.map(fy => (
                    <SelectItem key={fy} value={String(fy)}>FY{fy-1}-{String(fy%100).padStart(2,'0')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="w-px h-5 bg-slate-200" />

              <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5">
                {Object.entries(TIME_PERIODS).map(([key, cfg]) => (
                  <button key={key} onClick={() => { setTimePeriod(key); clearMonthRange(); }}
                    className={cn('px-2.5 py-0.5 text-xs rounded-md transition-all font-medium',
                      timePeriod === key ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700')}>
                    {cfg.label}
                  </button>
                ))}
              </div>

              {timePeriod === 'monthly' && (
                <>
                  <div className="w-px h-5 bg-slate-200" />
                  <DateRangePickerPopover
                    confirmed={dateRange}
                    onConfirm={(r) => setDateRange(r)}
                    fyStart={fyMonthStart[0]}
                    fyEnd={new Date(fyMonthStart[fyMonthStart.length - 1].getFullYear(), fyMonthStart[fyMonthStart.length - 1].getMonth() + 1, 0)}
                    visibleCount={visiblePeriods.length}
                  />
                </>
              )}
            </div>

            {/* Middle group: Channel + Category */}
            <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl px-2 py-1.5 shadow-sm">
              <Layers className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <MultiSelect values={selectedChannels} onValuesChange={setSelectedChannels}
                options={dynamicChannelOptions} placeholder="All Channels" className="w-40 border-0 shadow-none h-7 text-xs" />
              <div className="w-px h-5 bg-slate-200" />
              <BarChart3 className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <MultiSelect values={selectedCategories} onValuesChange={setSelectedCategories}
                options={dynamicCategoryOptions} placeholder="All Categories" className="w-40 border-0 shadow-none h-7 text-xs" />
            </div>

            {/* Right group: actions */}
            <div className="ml-auto flex items-center gap-1.5">
              <button
                onClick={() => setShowPct(p => !p)}
                className={cn(
                  'flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-xl border transition-all shadow-sm',
                  showPct
                    ? 'bg-primary-500 text-white border-indigo-600 shadow-indigo-200'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                )}
              >
                <Percent className="w-3.5 h-3.5" /> %
              </button>
              <div className="w-px h-5 bg-slate-200" />
              <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-500 hover:text-slate-700" onClick={() => loadData(selectedFiscalYear)}>
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
              </Button>
              <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-500 hover:text-slate-700" onClick={expandAll}>
                <Plus className="w-3.5 h-3.5 mr-1" />Expand
              </Button>
              <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-500 hover:text-slate-700" onClick={collapseAll}>
                <Minus className="w-3.5 h-3.5 mr-1" />Collapse
              </Button>
            </div>
          </div>

          {/* P&L Table */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="overflow-auto max-h-[calc(100vh-310px)]">
              <table className="w-full border-collapse min-w-max">
                <thead className="sticky top-0 z-20 bg-slate-100">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider sticky left-0 bg-slate-100 z-30 min-w-[260px]">
                      Line Item
                    </th>
                    {visiblePeriods.map((i) => (
                      <th key={i} className={cn('text-xs font-semibold text-slate-600 uppercase tracking-wider',
                        showAOPComparison ? 'p-0 min-w-[180px]' : 'px-2 py-2 text-right min-w-[90px]')}>
                        {showAOPComparison ? (
                          <div className="grid grid-cols-3 divide-x divide-slate-200">
                            <div className="px-1 py-2 text-center text-[9px] text-slate-400">AOP</div>
                            <div className="px-1 py-2 text-center text-[9px]">{periodConfig.format(i)}</div>
                            <div className="px-1 py-2 text-center text-[9px] text-slate-400">Var%</div>
                          </div>
                        ) : periodConfig.format(i)}
                      </th>
                    ))}
                    <th className={cn('text-xs font-semibold text-slate-600 uppercase tracking-wider bg-slate-200 border-l border-slate-300',
                      showAOPComparison ? 'p-0 min-w-[180px]' : 'px-2 py-2 text-right min-w-[100px]')}>
                      {showAOPComparison ? (
                        <div className="grid grid-cols-3 divide-x divide-slate-300">
                          <div className="px-1 py-2 text-center text-[9px]">AOP</div>
                          <div className="px-1 py-2 text-center text-[9px]">YTD</div>
                          <div className="px-1 py-2 text-center text-[9px]">Var%</div>
                        </div>
                      ) : 'YTD'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {!hasData ? (
                    <tr>
                      <td colSpan={visiblePeriods.length + 2} className="py-20 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <FileSpreadsheet className="w-12 h-12 text-slate-300" />
                          <p className="text-slate-500 font-medium">No actuals data for FY{selectedFiscalYear - 1}-{String(selectedFiscalYear % 100).padStart(2,'0')}</p>
                          <p className="text-slate-400 text-sm">Download the template, fill in your data, then import.</p>
                          <div className="flex gap-2 mt-1">
                            <Button variant="outline" size="sm" onClick={handleTemplateDownload}>
                              <Download className="w-4 h-4 mr-1.5" /> Download Template
                            </Button>
                            <Button size="sm" onClick={() => setShowImportDialog(true)}>
                              <Upload className="w-4 h-4 mr-1.5" /> Import Actuals
                            </Button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    PNL_STRUCTURE.map(item => renderRow(item, 0))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* ── Cash Flow tab ──────────────────────────────────────────────── */}
        <TabsContent value="cashflow" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
            <div className="flex flex-col items-center gap-3 text-slate-400">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center">
                <RefreshCw className="w-7 h-7 text-slate-300" />
              </div>
              <p className="font-semibold text-slate-600 text-lg">Cash Flow — Coming Soon</p>
              <p className="text-sm max-w-sm">
                Cash flow actuals will be available once the data ingestion pipeline for treasury
                and working capital is set up.
              </p>
              <div className="mt-2 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-500">
                Planned: Operating · Investing · Financing activities, Opening &amp; Closing Cash
              </div>
            </div>
          </div>
        </TabsContent>

        {/* ── Balance Sheet tab ──────────────────────────────────────────── */}
        <TabsContent value="balance" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
            <div className="flex flex-col items-center gap-3 text-slate-400">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center">
                <BarChart3 className="w-7 h-7 text-slate-300" />
              </div>
              <p className="font-semibold text-slate-600 text-lg">Balance Sheet — Coming Soon</p>
              <p className="text-sm max-w-sm">
                Balance sheet actuals will be available once the accounting data integration
                with Tally / SAP is configured.
              </p>
              <div className="mt-2 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-500">
                Planned: Assets · Liabilities · Equity with monthly snapshots
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Import dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-primary-600" /> Import Actuals
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-600">
              Upload an Excel file with <strong>17 channel sheets</strong> (MSOR, MNOW, SJIT, PPMP for Myntra)
              and an optional <strong>Overhead &amp; Marketing</strong> sheet.
            </p>
            <div
              className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center cursor-pointer hover:border-primary-400 transition-colors"
              onClick={() => importRef.current?.click()}
            >
              {importFile ? (
                <div className="flex items-center justify-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm font-medium text-slate-800">{importFile.name}</span>
                  <button onClick={e => { e.stopPropagation(); setImportFile(null); }} className="text-slate-400 hover:text-red-500">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">Click to browse or drag &amp; drop</p>
                  <p className="text-xs text-slate-400 mt-1">.xlsx files only</p>
                </>
              )}
            </div>
            <input ref={importRef} type="file" accept=".xlsx,.xls" className="hidden"
              onChange={e => setImportFile(e.target.files?.[0] || null)} />

            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">Fiscal Year:</span>
              <Select value={String(selectedFiscalYear)} onValueChange={v => setSelectedFiscalYear(parseInt(v))}>
                <SelectTrigger className="w-32 h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {availableFYs.map(fy => (
                    <SelectItem key={fy} value={String(fy)}>FY{fy-1}-{String(fy%100).padStart(2,'0')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>Cancel</Button>
            <Button onClick={handleTemplateDownload}
              variant="ghost" className="text-slate-600">
              <Download className="w-4 h-4 mr-1" /> Template
            </Button>
            <Button onClick={handleImport} disabled={!importFile || importing}>
              {importing ? <><RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> Importing…</> : 'Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
